import { motion, AnimatePresence } from 'framer-motion';
import { Crown } from 'lucide-react';
import { LESSON } from '@/constants/testIds';

const BacktrackingVisualization = ({ array, step, lessonId }) => {
  if (!step) return null;

  // ============ N-QUEENS BOARD ============
  if (lessonId === 'n-queens') {
    const board = step.board || [];
    const n = board.length;
    return (
      <div className="space-y-4">
        <div className="flex justify-center">
          <div 
            className="grid gap-0.5 p-2 bg-[#0F0F12] border border-white/10 rounded-lg"
            style={{ gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))` }}
          >
            {board.map((row, i) => row.map((cell, j) => {
              const isTry = step.tryPosition?.row === i && step.tryPosition?.col === j;
              const isPlaced = step.placedPosition?.row === i && step.placedPosition?.col === j;
              const isBacktrack = step.backtrackPosition?.row === i && step.backtrackPosition?.col === j;
              const isUnsafe = step.unsafePosition?.row === i && step.unsafePosition?.col === j;
              const isChecker = (i + j) % 2 === 0;
              
              let bgClass = isChecker ? 'bg-[#1A1A20]' : 'bg-[#2A2A30]';
              let borderClass = 'border-white/10';
              
              if (isUnsafe) {
                bgClass = 'bg-red-500/30';
                borderClass = 'border-red-500';
              } else if (isBacktrack) {
                bgClass = 'bg-orange-500/30';
                borderClass = 'border-orange-500';
              } else if (isPlaced || isTry) {
                bgClass = 'bg-[#FFB800]/20';
                borderClass = 'border-[#FFB800]';
              }

              return (
                <div
                  key={`${i}-${j}`}
                  data-testid={`queen-cell-${i}-${j}`}
                  className={`w-12 h-12 flex items-center justify-center border-2 rounded ${bgClass} ${borderClass}`}
                >
                  {cell === 1 && (
                    <motion.div
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                    >
                      <Crown className="w-6 h-6 text-[#FFB800] fill-[#FFB800]" />
                    </motion.div>
                  )}
                </div>
              );
            }))}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="bg-[#0F0F12] border border-[#FFB800]/20 rounded-lg p-3 text-center">
            <div className="text-[#FFB800] text-xs font-code mb-1">ATTEMPTS</div>
            <div className="text-white text-xl font-code font-bold">{step.attempts || 0}</div>
          </div>
          <div className="bg-[#0F0F12] border border-orange-500/20 rounded-lg p-3 text-center">
            <div className="text-orange-400 text-xs font-code mb-1">BACKTRACKS</div>
            <div className="text-white text-xl font-code font-bold">{step.backtracks || 0}</div>
          </div>
          <div className="bg-[#0F0F12] border border-green-500/20 rounded-lg p-3 text-center">
            <div className="text-green-400 text-xs font-code mb-1">SOLUTIONS</div>
            <div className="text-white text-xl font-code font-bold">{step.solutions?.length || 0}</div>
          </div>
        </div>

        {step.result !== undefined && (
          <div className="bg-gradient-to-r from-green-500/10 to-green-400/10 border border-green-500/30 rounded-lg p-4 text-center">
            <div className="text-[#A1A1AA] text-sm font-body mb-1">Total Solutions Found</div>
            <div className="text-green-400 text-3xl font-code font-bold">{step.result}</div>
          </div>
        )}
      </div>
    );
  }

  // ============ SUBSETS / PERMUTATIONS ARRAY VIEW ============
  const currentArray = step.array || array || [];
  const includedIndices = step.included || [];
  const swapping = step.swapping || [];
  const fixedIndex = step.fixedIndex ?? -1;

  const getCellStyle = (index) => {
    const baseStyle = "w-14 h-14 flex items-center justify-center border-2 rounded-md text-lg font-code relative";
    
    if (swapping.includes(index)) {
      return `${baseStyle} border-red-500 bg-red-500/20 text-red-400 shadow-[0_0_15px_rgba(239,68,68,0.5)]`;
    }
    if (lessonId === 'permutations' && index < fixedIndex) {
      return `${baseStyle} border-green-500 bg-green-500/20 text-green-400`;
    }
    if (lessonId === 'permutations' && index === fixedIndex) {
      return `${baseStyle} border-[#FFB800] bg-[#FFB800]/20 text-[#FFB800] shadow-[0_0_15px_rgba(255,184,0,0.5)]`;
    }
    if (lessonId === 'subsets' && includedIndices.includes(index)) {
      return `${baseStyle} border-[#FFB800] bg-[#FFB800]/20 text-[#FFB800] shadow-[0_0_15px_rgba(255,184,0,0.5)]`;
    }
    if (lessonId === 'subsets' && step.currentIndex === index) {
      return `${baseStyle} border-[#007AFF] bg-[#007AFF]/10 text-[#007AFF]`;
    }
    return `${baseStyle} border-white/20 bg-[#1A1A20] text-white`;
  };

  return (
    <div className="space-y-4">
      {/* Array Display */}
      <div className="flex flex-wrap gap-2 justify-center items-center">
        {currentArray.map((value, index) => (
          <motion.div
            key={index}
            data-testid={LESSON.arrayCell(index)}
            className={getCellStyle(index)}
            layout
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 200, damping: 20 }}
          >
            <div className="absolute -top-4 text-[9px] text-[#71717A] font-code">
              {index}
            </div>
            <div>{value}</div>
          </motion.div>
        ))}
      </div>

      {/* Current Subset/Permutation */}
      {step.current !== undefined && (
        <div className="bg-[#0F0F12] border border-[#FFB800]/20 rounded-lg p-3">
          <div className="text-[#FFB800] text-xs font-code mb-2">CURRENT {lessonId === 'subsets' ? 'SUBSET' : 'PERMUTATION'}</div>
          <div className="font-code text-white text-sm">
            [{step.current.join(', ')}]
          </div>
        </div>
      )}

      {/* Found Solutions List */}
      {(step.subsets || step.permutations) && (
        <div className="bg-[#0F0F12] border border-white/10 rounded-lg p-3 max-h-40 overflow-y-auto">
          <div className="text-[#007AFF] text-xs font-code mb-2">
            {lessonId === 'subsets' ? 'SUBSETS' : 'PERMUTATIONS'} FOUND ({(step.subsets || step.permutations).length})
          </div>
          <div className="flex flex-wrap gap-1">
            <AnimatePresence>
              {(step.subsets || step.permutations).map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-[#1A1A20] border border-white/10 rounded px-2 py-1 text-xs font-code text-[#A1A1AA]"
                >
                  [{item.join(',')}]
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
      )}

      {/* Result */}
      {step.result !== undefined && (
        <div className="bg-gradient-to-r from-green-500/10 to-green-400/10 border border-green-500/30 rounded-lg p-4 text-center">
          <div className="text-[#A1A1AA] text-sm font-body mb-1">Total {lessonId === 'subsets' ? 'Subsets' : 'Permutations'}</div>
          <div className="text-green-400 text-3xl font-code font-bold">{step.result}</div>
        </div>
      )}
    </div>
  );
};

export default BacktrackingVisualization;
